import { StructuredDescription } from "@/models/EditorData"
import { User } from "@/models/User"
import { formatDateAgo, parseDate } from "@/utils/dateUtil"

import { Notification, NotificationAttachments } from "./Notification"

export enum IdeaActivityEventType {
  CREATED = "CREATED",
  UPDATED = "UPDATED",
  PROMOTED_TO_FEATURE = "PROMOTED_TO_FEATURE",
  CONVERTED_TO_EPIC = "CONVERTED_TO_EPIC",
  CONVERTED_TO_ITEM = "CONVERTED_TO_ITEM"
}

export interface IdeaActivityEvent {
  type: IdeaActivityEventType
  ideaSequenceNumber: number
  ideaTitle: string
  projectName?: string
  targetSequenceNumber?: number | null
  targetUrl?: string | null
  ideaUrl?: string | null
  occurredAt: string
  initiator?: User
  attachmentsMetadata?: {
    attachments: NotificationAttachments[]
    attachmentsCount: number
  }
  titleChanged?: {
    oldValue: string
    newValue: string
  }
  labelChanged?: {
    oldValue: string
    newValue: string
  }
  priorityChanged?: {
    oldValue: number
    newValue: number
  }
  structuredDescriptionChanged?: {
    oldValue: StructuredDescription
    newValue: StructuredDescription
  }
  ownerChanged?: {
    oldValue: User
    newValue: User
  }
}

type IdeaActivityContent = Partial<IdeaActivityEvent> & {
  events?: IdeaActivityEvent[]
}

const displayedEventTypes = new Set<IdeaActivityEventType>([
  IdeaActivityEventType.CREATED,
  IdeaActivityEventType.UPDATED,
  IdeaActivityEventType.PROMOTED_TO_FEATURE,
  IdeaActivityEventType.CONVERTED_TO_EPIC,
  IdeaActivityEventType.CONVERTED_TO_ITEM
])

export class NotificationIdeaActivity extends Notification {
  selectedEvent?: IdeaActivityEvent

  constructor(props) {
    super(props)
    this.selectedEvent = props?.selectedEvent
  }

  get ideaActivityContent(): IdeaActivityContent {
    return this.content as IdeaActivityContent
  }

  get displayEvents(): IdeaActivityEvent[] {
    const content = this.ideaActivityContent
    const events = Array.isArray(content.events)
      ? content.events
      : content.type
        ? [content as IdeaActivityEvent]
        : []

    return events.filter(event => displayedEventTypes.has(event.type))
  }

  get displayEvent(): IdeaActivityEvent | undefined {
    return this.selectedEvent ?? this.displayEvents[0]
  }

  get ideaTitle(): string {
    return this.displayEvent?.ideaTitle ?? ""
  }

  get ideaKey(): string {
    const sequenceNumber = this.displayEvent?.ideaSequenceNumber
    return sequenceNumber != null ? `I-${sequenceNumber}` : ""
  }

  get initiatorFullName(): string {
    const initiator = this.displayEvent?.initiator
    if (!initiator) return ""
    return initiator.firstName + " " + initiator.lastName
  }

  get attachments(): NotificationAttachments[] {
    return this.displayEvent?.attachmentsMetadata?.attachments ?? []
  }

  get attachmentsCount(): number {
    return this.displayEvent?.attachmentsMetadata?.attachmentsCount ?? 0
  }

  get descriptionHtml(): string {
    return this.displayEvent?.structuredDescriptionChanged?.newValue?.html ?? ""
  }

  get projectName(): string {
    return this.displayEvent?.projectName ?? super.projectName
  }

  get resourceUrl(): string {
    return this.displayEvent?.targetUrl ?? ""
  }

  get ideaUrl(): string {
    return this.displayEvent?.ideaUrl ?? ""
  }

  get timeAgo(): string {
    const occurredAt = this.displayEvent?.occurredAt
    if (!occurredAt) return super.timeAgo

    return formatDateAgo({
      date: new Date(),
      dateFormat: parseDate(occurredAt, true),
      short: true,
      sufixAgo: true
    })
  }
}
